import React, {useState} from "react";
import {DictionaryItemListing} from "../../admin/core/types";
import {Panel} from "primereact/panel";
import AdminRichTextArea from "./AdminRichTextArea";
import {getTranslatedLanguages, getValue, setValue, StringTranslations} from "../../common/types/StringTranslations";
import FieldLanguageSwitcher from "./FieldLanguageSwitcher";
import {useTranslation} from "react-i18next";

interface AdminTextSectionsProps {
  title: string;
  sectionsContent: Map<string, StringTranslations>;
  sectionTypes: DictionaryItemListing[];
  handleSectionContentChange: (typeAlias: string, content: StringTranslations) => void;
}

const hasNonEmptyContent = (translations: StringTranslations | undefined): boolean => {
  if (!translations) return false;
  return Object.values(translations).some(content => content && content.trim().length > 0);
};

const AdminTextSections: React.FC<AdminTextSectionsProps> = ({
                                                               title,
                                                               sectionsContent,
                                                               sectionTypes,
                                                               handleSectionContentChange
                                                             }) => {
  const {i18n} = useTranslation();

  // Track current language per section type
  const [currentLanguages, setCurrentLanguages] = useState<Map<string, string>>(
    new Map(sectionTypes.map(type => [type.alias, i18n.language]))
  );

  const handleLanguageChange = (typeAlias: string, language: string) => {
    setCurrentLanguages(prev => new Map(prev).set(typeAlias, language));
  };

  const handleContentChange = (typeAlias: string, content: string) => {
    const currentLang = currentLanguages.get(typeAlias) || i18n.language;
    const currentTranslations = sectionsContent.get(typeAlias) || {};
    const updatedTranslations = setValue(currentTranslations, currentLang, content);
    handleSectionContentChange(typeAlias, updatedTranslations);
  };

  // Count distinct section types with non-empty content
  const nonEmptySectionCount = sectionTypes.filter(type =>
    hasNonEmptyContent(sectionsContent.get(type.alias))
  ).length;

  const headerTemplate = (
    <span className="flex items-center gap-2">
      {title}
      {nonEmptySectionCount > 0 && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-700 text-white font-bold">
          {nonEmptySectionCount}
        </span>
      )}
    </span>
  );

  return (
    <Panel
      header={headerTemplate}
      toggleable
      collapsed={true}
      pt={{
        header: {
          className: 'text-lg font-semibold text-gray-900 mb-4'
        }
      }}>
      {sectionTypes.map((sectionType) => {
        const currentLang = currentLanguages.get(sectionType.alias) || i18n.language;
        const translations = sectionsContent.get(sectionType.alias) || {};
        const currentValue = getValue(translations, currentLang);
        const translatedLanguages = getTranslatedLanguages(translations);

        return (
          <AdminRichTextArea
            key={sectionType.alias}
            value={currentValue}
            onChange={(html) => handleContentChange(sectionType.alias, html)}
            name={`section-${sectionType.alias}`}
            id={`section-${sectionType.alias}`}
            label={sectionType.value}
            rows={12}
            languageSwitcher={
              <FieldLanguageSwitcher
                currentLanguage={currentLang}
                onLanguageChange={(lang) => handleLanguageChange(sectionType.alias, lang)}
                collapsed={true}
                translatedLanguages={translatedLanguages}
              />
            }
          />
        );
      })}
    </Panel>
  );
};

export default AdminTextSections;